import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../utils/AuthContext';
import { snapshotConsoleLogs, type ConsoleLogEntry } from '../../utils/consoleLogBuffer';
import { createCrmFeedback } from '../../utils/crmFeedbackService';
import type { CrmFeedbackType } from '../../types/crmFeedback';
import FeedbackModal from './FeedbackModal';
import './FeedbackModal.css';

interface CaptureState {
  screenshotDataUrl: string | null;
  screenshotBlob: Blob | null;
  logs: ConsoleLogEntry[];
  pageUrl: string;
  userAgent: string;
  viewport: { w: number; h: number };
}

interface SubmitArgs {
  type: CrmFeedbackType;
  description: string;
  includeLogs: boolean;
}

interface FeedbackContextValue {
  open: () => void;
  close: () => void;
  isOpen: boolean;
  capturing: boolean;
  capture: CaptureState | null;
  submitting: boolean;
  error: string | null;
  submit: (args: SubmitArgs) => Promise<void>;
  setScreenshot: (dataUrl: string | null, blob: Blob | File | null) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

const NOOP_FEEDBACK: FeedbackContextValue = {
  open: () => {},
  close: () => {},
  isOpen: false,
  capturing: false,
  capture: null,
  submitting: false,
  error: null,
  submit: async () => {},
  setScreenshot: () => {},
};

export function useFeedback(): FeedbackContextValue {
  return useContext(FeedbackContext) ?? NOOP_FEEDBACK;
}

async function captureViewport(): Promise<{ dataUrl: string | null; blob: Blob | null }> {
  const root = document.getElementById('root');
  if (!root) return { dataUrl: null, blob: null };
  document.documentElement.classList.add('crm-feedback-capturing');
  try {
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
    const { toJpeg } = await import('html-to-image');
    const dataUrl = await toJpeg(root, {
      quality: 0.7,
      pixelRatio: 1,
      cacheBust: true,
      filter: node => {
        if (!(node instanceof Element)) return true;
        return !node.closest('.crm-feedback-btn, .crm-feedback-root');
      },
    });
    const blob = await (await fetch(dataUrl)).blob();
    if (blob.size > 2 * 1024 * 1024) return { dataUrl, blob: null };
    return { dataUrl, blob };
  } catch {
    return { dataUrl: null, blob: null };
  } finally {
    document.documentElement.classList.remove('crm-feedback-capturing');
  }
}

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [capture, setCapture] = useState<CaptureState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const openingRef = useRef(false);
  const toastTimer = useRef<number | null>(null);

  const close = useCallback(() => {
    if (submitting) return;
    setIsOpen(false);
    setCapturing(false);
    setCapture(null);
    setError(null);
    openingRef.current = false;
  }, [submitting]);

  const open = useCallback(async () => {
    if (openingRef.current || submitting) return;
    openingRef.current = true;
    setError(null);
    setCapturing(true);
    const logs = snapshotConsoleLogs();
    const pageUrl = window.location.href;
    const userAgent = navigator.userAgent;
    const viewport = { w: window.innerWidth, h: window.innerHeight };
    const shot = await captureViewport();
    setCapture({
      screenshotDataUrl: shot.dataUrl,
      screenshotBlob: shot.blob,
      logs,
      pageUrl,
      userAgent,
      viewport,
    });
    setCapturing(false);
    setIsOpen(true);
    openingRef.current = false;
  }, [submitting]);

  const setScreenshot = useCallback((dataUrl: string | null, blob: Blob | File | null) => {
    setCapture(prev => prev ? { ...prev, screenshotDataUrl: dataUrl, screenshotBlob: blob } : prev);
  }, []);

  const submit = useCallback(async ({ type, description, includeLogs }: SubmitArgs) => {
    if (!user || !capture) return;
    const trimmed = description.trim();
    if (!trimmed) {
      setError('Please add a short description.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const keepShot = type === 'bug' || !!capture.screenshotBlob;
      await createCrmFeedback({
        type,
        description: trimmed,
        pageUrl: capture.pageUrl,
        userAgent: capture.userAgent,
        viewport: capture.viewport,
        logs: includeLogs ? capture.logs : [],
        reporterId: user.id,
        reporterName: user.name || '',
        reporterEmail: user.email || '',
        reporterRole: user.role,
        screenshotBlob: keepShot ? capture.screenshotBlob : null,
      });
      setIsOpen(false);
      setCapture(null);
      setToast(type === 'bug' ? 'Thanks — we got your report.' : 'Thanks — feature request sent.');
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
      toastTimer.current = window.setTimeout(() => setToast(null), 3200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send feedback.');
    } finally {
      setSubmitting(false);
    }
  }, [user, capture]);

  const value = useMemo<FeedbackContextValue>(() => ({
    open,
    close,
    isOpen,
    capturing,
    capture,
    submitting,
    error,
    submit,
    setScreenshot,
  }), [open, close, isOpen, capturing, capture, submitting, error, submit, setScreenshot]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <div className="crm-feedback-root">
        <FeedbackModal />
        {toast && <div className="crm-feedback-toast" role="status">{toast}</div>}
      </div>
    </FeedbackContext.Provider>
  );
};

export default FeedbackProvider;
